import React, { useState } from 'react';
import { CareerCheckBox } from '../CareerCheckBox';
import CareerFilterTag from './CareerFilterTag';
import { useLayoutSize, WrappedMessage } from 'utils';
import CollapsibleComponent, {
  CollapsibleSize,
} from '../CareerCollapsible/CollapsibleComponent';
import FilterHeader from './FilterSearch';
import { IconSymbol } from 'elements';
import { MessageDescriptor } from 'react-intl';
import { intl } from 'i18n';
import messages from '../../displayMessages';
import { CareerTagData } from '../CareerHome/career-search';

interface CareerFilterProps {
  label: MessageDescriptor;
  filterList: CareerTagData[];
  selectedFilters: CareerTagData[];
  onFilterSelect: (filter: CareerTagData) => void;
  onReset: () => void;
  closeCollapsible?: (id: string) => void;
  iconName?: IconSymbol;
  resultCount?: number;
  disableSearch?: boolean;
}

const CareerFilter: React.FC<CareerFilterProps> = ({
  label,
  filterList,
  selectedFilters,
  onFilterSelect,
  onReset,
  closeCollapsible,
  iconName,
  resultCount,
  disableSearch,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const isMobile = ['small', 'mobile'].includes(useLayoutSize());
  let buttonCountMessage = messages.showCareersButtonWithCountTextPlural;

  if (resultCount === 1) {
    buttonCountMessage = messages.showCareersButtonWithCountText;
  }

  let filterLabel = '';
  if (selectedFilters.length > 0) {
    const firstFilter = selectedFilters[0];
    const filterName = firstFilter.name;
    filterLabel =
      selectedFilters.length > 1
        ? filterName + ` +${selectedFilters.length - 1}`
        : filterName;
  }

  const searchFilters = (query: string) => {
    setSearchQuery(query);
  };

  const applyFilterSearch = (filters: CareerTagData[]) => {
    if (!searchQuery) {
      return filters;
    }

    return filters.filter((filter) => {
      const matches = filter.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const childMatches =
        filter.childTags &&
        filter.childTags.some((childFilter: CareerTagData) =>
          childFilter.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      if (childMatches) {
        filter.opened = true;
      }
      return matches || childMatches;
    });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setSearchQuery('');

    filterList.forEach((filter) => {
      const childSelected =
        filter.childTags &&
        filter.childTags.some((childFilter: CareerTagData) =>
          selectedFilters.some((selected) => selected.id === childFilter.id)
        );
      if (filter.opened && !childSelected && closeCollapsible) {
        closeCollapsible(filter.id);
      }
    });
  };

  const getCheckbox = (filter: CareerTagData) => (
    <CareerCheckBox
      checked={selectedFilters.some((selected) => selected.id === filter.id)}
      onClick={() => onFilterSelect(filter)}
      disableTooltip
    >
      {filter.name}
    </CareerCheckBox>
  );

  return (
    <div className='career-filter'>
      <CareerFilterTag
        iconName={iconName}
        onClick={toggleDropdown}
        onClearFilter={!isDropdownOpen && filterLabel ? onReset : undefined}
        isDropdownOpen={isDropdownOpen}
      >
        {!isDropdownOpen && filterLabel ? (
          filterLabel
        ) : (
          <WrappedMessage message={label} />
        )}
      </CareerFilterTag>
      {isDropdownOpen && (
        <div className='filter-dropdown'>
          <FilterHeader
            filterLabel={label}
            onSubmit={searchFilters}
            onReset={onReset}
            onClose={toggleDropdown}
            disableSearch={disableSearch}
          />
          <div className='filter-options'>
            {applyFilterSearch(filterList).map((filter) =>
              filter.childTags && filter.childTags.length > 0 ? (
                <CollapsibleComponent
                  id={filter.id}
                  size={CollapsibleSize.xLarge}
                  title=''
                  headerChildren={getCheckbox(filter)}
                  key={filter.id}
                  opened={filter.opened}
                  onClickCallback={closeCollapsible}
                >
                  <div className='child-options'>
                    {filter.childTags &&
                      filter.childTags.map((childFilter: any) => {
                        if (
                          searchQuery &&
                          !childFilter.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        ) {
                          return null;
                        }
                        return (
                          <div key={childFilter.id} className='child-option'>
                            {getCheckbox(childFilter)}
                          </div>
                        );
                      })}
                  </div>
                </CollapsibleComponent>
              ) : (
                <div key={filter.id} className='career-filter-option'>
                  {getCheckbox(filter)}
                </div>
              )
            )}
          </div>
          {isMobile && (
            <div className='footer'>
              <button className='show-all-button' onClick={toggleDropdown}>
                {selectedFilters.length > 0
                  ? intl.formatMessage(buttonCountMessage, {
                      count: resultCount,
                    })
                  : intl.formatMessage(messages.showAllCareersButtonText)}
              </button>
            </div>
          )}
        </div>
      )}
      {isDropdownOpen && (
        <div className='dropdown-overlay' onClick={toggleDropdown} />
      )}
    </div>
  );
};

export default CareerFilter;
